/**
 * Client-Focused Field Placeholders
 * Simplified list focusing on client signatures, initials, dates, and names
 */

export interface ClientFieldPlaceholder {
  id: string;
  label: string;
  category: string;
  fieldType: 'text' | 'signature' | 'date' | 'initial';
  priority: 'high' | 'medium' | 'low';
}

export const CLIENT_FIELD_PLACEHOLDERS: ClientFieldPlaceholder[] = [
  // Buyer Fields - HIGH PRIORITY
  { id: 'buyer1_signature', label: 'Buyer 1 Signature', category: 'Buyer', fieldType: 'signature', priority: 'high' },
  { id: 'buyer1_name', label: 'Buyer 1 Name (Print)', category: 'Buyer', fieldType: 'text', priority: 'high' },
  { id: 'buyer1_date', label: 'Buyer 1 Date', category: 'Buyer', fieldType: 'date', priority: 'high' },
  { id: 'buyer1_initial', label: 'Buyer 1 Initial', category: 'Buyer', fieldType: 'initial', priority: 'medium' },
  
  { id: 'buyer2_signature', label: 'Buyer 2 Signature', category: 'Buyer', fieldType: 'signature', priority: 'high' },
  { id: 'buyer2_name', label: 'Buyer 2 Name (Print)', category: 'Buyer', fieldType: 'text', priority: 'high' },
  { id: 'buyer2_date', label: 'Buyer 2 Date', category: 'Buyer', fieldType: 'date', priority: 'high' },
  { id: 'buyer2_initial', label: 'Buyer 2 Initial', category: 'Buyer', fieldType: 'initial', priority: 'medium' },
  
  // Seller Fields - HIGH PRIORITY
  { id: 'seller1_signature', label: 'Seller 1 Signature', category: 'Seller', fieldType: 'signature', priority: 'high' },
  { id: 'seller1_name', label: 'Seller 1 Name (Print)', category: 'Seller', fieldType: 'text', priority: 'high' },
  { id: 'seller1_date', label: 'Seller 1 Date', category: 'Seller', fieldType: 'date', priority: 'high' },
  { id: 'seller1_initial', label: 'Seller 1 Initial', category: 'Seller', fieldType: 'initial', priority: 'medium' },
  
  { id: 'seller2_signature', label: 'Seller 2 Signature', category: 'Seller', fieldType: 'signature', priority: 'high' },
  { id: 'seller2_name', label: 'Seller 2 Name (Print)', category: 'Seller', fieldType: 'text', priority: 'high' },
  { id: 'seller2_date', label: 'Seller 2 Date', category: 'Seller', fieldType: 'date', priority: 'high' },
  { id: 'seller2_initial', label: 'Seller 2 Initial', category: 'Seller', fieldType: 'initial', priority: 'medium' },
  
  // Additional Parties (if needed)
  { id: 'buyer3_signature', label: 'Buyer 3 Signature', category: 'Additional', fieldType: 'signature', priority: 'low' },
  { id: 'buyer3_name', label: 'Buyer 3 Name (Print)', category: 'Additional', fieldType: 'text', priority: 'low' },
  { id: 'seller3_signature', label: 'Seller 3 Signature', category: 'Additional', fieldType: 'signature', priority: 'low' },
  { id: 'seller3_name', label: 'Seller 3 Name (Print)', category: 'Additional', fieldType: 'text', priority: 'low' },
  
  // Generic fallbacks
  { id: 'client_signature', label: 'Client Signature', category: 'Generic', fieldType: 'signature', priority: 'medium' },
  { id: 'client_name', label: 'Client Name', category: 'Generic', fieldType: 'text', priority: 'medium' },
  { id: 'signature_date', label: 'Date', category: 'Generic', fieldType: 'date', priority: 'medium' },
  { id: 'client_initial', label: 'Initial', category: 'Generic', fieldType: 'initial', priority: 'medium' }
];

// Helper to get high priority fields only
export function getHighPriorityFields(): ClientFieldPlaceholder[] {
  return CLIENT_FIELD_PLACEHOLDERS.filter(f => f.priority === 'high');
}

// Helper to get fields by party type
export function getFieldsByParty(party: 'Buyer' | 'Seller'): ClientFieldPlaceholder[] {
  return CLIENT_FIELD_PLACEHOLDERS.filter(f => f.category === party);
}

// Training helper - compare blank vs signed documents
export interface FieldComparison {
  fieldId: string;
  blankCoordinates: { x: number; y: number; width: number; height: number };
  signedCoordinates?: { x: number; y: number; width: number; height: number };
  isFilled: boolean;
  confidence: number;
}

export function detectFilledFields(
  blankFields: any[],
  signedFields: any[]
): FieldComparison[] {
  // This would compare blank document fields with signed document fields
  // to help train the AI on what filled fields look like
  return blankFields.map(blankField => {
    const signedField = signedFields.find(sf => 
      Math.abs(sf.x - blankField.x) < 5 && 
      Math.abs(sf.y - blankField.y) < 5
    );
    
    return {
      fieldId: blankField.id,
      blankCoordinates: {
        x: blankField.x,
        y: blankField.y,
        width: blankField.width,
        height: blankField.height
      },
      signedCoordinates: signedField ? {
        x: signedField.x,
        y: signedField.y,
        width: signedField.width,
        height: signedField.height
      } : undefined,
      isFilled: !!signedField,
      confidence: signedField ? signedField.confidence : 0
    };
  });
}